/**
 * DropdownField component.
 *
 * Select dropdown with configurable options from the backend.
 */

import type { FormFieldDefinition } from "../../types";

export interface DropdownFieldOptions {
  field: FormFieldDefinition;
  onChange: (name: string, value: string) => void;
}

export class DropdownField {
  private readonly element: HTMLElement;
  private readonly selectEl: HTMLSelectElement;
  private readonly errorEl: HTMLElement;

  constructor(private readonly options: DropdownFieldOptions) {
    const { field } = options;

    this.element = document.createElement("div");
    this.element.className = "hana-form-field";

    // Label
    const labelEl = document.createElement("label");
    labelEl.className = "hana-form-field__label";
    labelEl.setAttribute("for", `hana-field-${field.name}`);
    labelEl.textContent = field.label;
    if (field.required) {
      const asterisk = document.createElement("span");
      asterisk.className = "hana-form-field__required";
      asterisk.textContent = " *";
      asterisk.setAttribute("aria-hidden", "true");
      labelEl.appendChild(asterisk);
    }
    this.element.appendChild(labelEl);

    // Select
    this.selectEl = document.createElement("select");
    this.selectEl.id = `hana-field-${field.name}`;
    this.selectEl.className = "hana-form-field__select";
    this.selectEl.name = field.name;
    this.selectEl.setAttribute(
      "aria-label",
      field.label + (field.required ? " (required)" : ""),
    );
    if (field.required) {
      this.selectEl.setAttribute("aria-required", "true");
    }

    // Placeholder option
    const placeholderOpt = document.createElement("option");
    placeholderOpt.value = "";
    placeholderOpt.textContent = field.placeholder ?? "Select...";
    placeholderOpt.disabled = true;
    this.selectEl.appendChild(placeholderOpt);

    // Add options.
    //
    // Tolerates a bare string as well as {value, label}. A backend that sends
    // ["Male", "Female"] instead of [{value,label}] otherwise renders a
    // dropdown of blank rows: `opt.value` and `opt.label` are both undefined,
    // nothing errors, the wire carries a 200, and the only symptom is a
    // select that opens onto nothing. That shipped to UAT once.
    if (field.options) {
      for (const opt of field.options) {
        const value = typeof opt === "string" ? opt : opt.value;
        const label = typeof opt === "string" ? opt : (opt.label ?? opt.value);
        if (value === undefined || label === undefined) {
          // Never append a blank row — an option the user cannot read or
          // select is worse than an absent one, because it looks like the
          // list loaded.
          console.error(
            "[hana] dropdown option missing value/label; skipping",
            { field: field.name, option: opt },
          );
          continue;
        }
        const optEl = document.createElement("option");
        optEl.value = value;
        optEl.textContent = label;
        this.selectEl.appendChild(optEl);
      }
    }

    // Set pre-populated value or select placeholder
    if (field.value) {
      this.selectEl.value = field.value;
    } else {
      placeholderOpt.selected = true;
    }

    this.selectEl.addEventListener("change", () => {
      this.clearError();
      this.options.onChange(field.name, this.selectEl.value);
    });

    this.element.appendChild(this.selectEl);

    // Error message
    this.errorEl = document.createElement("div");
    this.errorEl.className = "hana-form-field__error";
    this.errorEl.setAttribute("role", "alert");
    this.errorEl.setAttribute("aria-live", "polite");
    this.element.appendChild(this.errorEl);
  }

  getElement(): HTMLElement {
    return this.element;
  }

  getValue(): string {
    return this.selectEl.value;
  }

  setError(message: string): void {
    this.errorEl.textContent = message;
    this.selectEl.classList.add("hana-form-field__select--error");
    this.selectEl.setAttribute("aria-invalid", "true");
  }

  clearError(): void {
    this.errorEl.textContent = "";
    this.selectEl.classList.remove("hana-form-field__select--error");
    this.selectEl.removeAttribute("aria-invalid");
  }

  setDisabled(disabled: boolean): void {
    this.selectEl.disabled = disabled;
  }

  focus(): void {
    this.selectEl.focus();
  }
}
